/**
 * The material on screen. A PDF is drawn page by page with pdf.js, in the
 * browser, from the file the user just chose or from the sealed copy on the
 * server, so it is visible before the server has read a word. An image is
 * shown as it is. A Word file has no page picture, so its text is laid out
 * from the measured lines once the read is done.
 *
 * Findings carry a page and a box in PDF points (origin bottom left); each
 * one is drawn over the page it points to, and lands with a small pop when
 * it is new. Hovering a finding elsewhere scrolls its page into view.
 */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).toString();

const COLOR = { block: '#B42318', fix: '#E5601F', note: '#94a3b8' };

export default function DocView({ src, mime = '', findings = [], layout = null, focusPage = null, focusFindingId = null, onPageCount, height = 'calc(100vh - 260px)', width = 640 }) {
  const isPdf = /pdf/i.test(mime) || /\.pdf$/i.test(src?.name || '');
  const isImage = /image\//i.test(mime);
  if (isPdf) return <PdfPages src={src} findings={findings} focusPage={focusPage} focusFindingId={focusFindingId} onPageCount={onPageCount} height={height} width={width} />;
  if (isImage) return <ImagePage src={src} findings={findings} layout={layout} width={width} height={height} />;
  return <TextPages layout={layout} findings={findings} focusPage={focusPage} height={height} width={width} />;
}

function useObjectUrl(src) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!src) return undefined;
    if (typeof src === 'string') { setUrl(src); return undefined; }
    const u = URL.createObjectURL(src); setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [src]);
  return url;
}

async function loadBytes(src) {
  if (!src) return null;
  if (typeof src === 'string') { const r = await fetch(src, { credentials: 'include' }); if (!r.ok) throw new Error(`The file could not be fetched (${r.status})`); return new Uint8Array(await r.arrayBuffer()); }
  return new Uint8Array(await src.arrayBuffer());
}

function PdfPages({ src, findings, focusPage, focusFindingId, onPageCount, height, width }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [sizes, setSizes] = useState([]); // [{page, width, height}] in points
  const scroller = useRef(null);
  const pageRefs = useRef({});

  useEffect(() => {
    let dead = false; let task = null;
    setDoc(null); setErr(null); setSizes([]);
    (async () => {
      try {
        const data = await loadBytes(src);
        if (!data || dead) return;
        task = pdfjs.getDocument({ data, isEvalSupported: false, disableFontFace: false });
        const d = await task.promise;
        if (dead) return;
        const s = [];
        for (let i = 1; i <= d.numPages; i++) { const p = await d.getPage(i); const vp = p.getViewport({ scale: 1 }); s.push({ page: i, width: vp.width, height: vp.height }); }
        if (dead) return;
        setSizes(s); setDoc(d); onPageCount && onPageCount(d.numPages);
      } catch (e) { if (!dead) setErr(String(e?.message || e)); }
    })();
    return () => { dead = true; try { task?.destroy(); } catch { /* ignore */ } };
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!focusPage) return;
    const el = pageRefs.current[focusPage];
    if (el && scroller.current) scroller.current.scrollTo({ top: el.offsetTop - 12, behavior: 'smooth' });
  }, [focusPage, sizes.length]);

  if (err) return <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">The page could not be drawn: {err}</div>;
  return (
    <div ref={scroller} className="overflow-auto rounded-xl bg-slate-100 border border-slate-200 p-3" style={{ maxHeight: height }}>
      {!doc && <div className="grid gap-3">{[1, 2].map((i) => <div key={i} className="mx-auto bg-white rounded-md shadow-sm animate-pulse" style={{ width, height: width * 1.294 }} />)}</div>}
      {doc && sizes.map((s) => (
        <div key={s.page} ref={(el) => { pageRefs.current[s.page] = el; }} className="mx-auto mb-3 last:mb-0" style={{ width }}>
          <PdfPage doc={doc} page={s.page} size={s} width={width} findings={findings.filter((f) => f.page === s.page && f.bbox)} focusFindingId={focusFindingId} />
          <div className="text-[10px] text-slate-400 text-center mt-1">Page {s.page} of {sizes.length}</div>
        </div>
      ))}
    </div>
  );
}

function PdfPage({ doc, page, size, width, findings, focusFindingId }) {
  const canvasRef = useRef(null);
  const [vp, setVp] = useState(null);
  const scale = width / size.width;
  useEffect(() => {
    let dead = false; let render = null;
    (async () => {
      const p = await doc.getPage(page);
      if (dead) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const viewport = p.getViewport({ scale: scale * dpr });
      const canvas = canvasRef.current; if (!canvas) return;
      canvas.width = Math.floor(viewport.width); canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${width}px`; canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;
      render = p.render({ canvasContext: canvas.getContext('2d', { alpha: false }), viewport });
      try { await render.promise; } catch { /* cancelled */ }
      if (!dead) setVp(p.getViewport({ scale }));
    })();
    return () => { dead = true; try { render?.cancel(); } catch { /* ignore */ } };
  }, [doc, page, scale, width]);
  const seen = useRef(new Set());
  const marks = useMemo(() => findings.map((f) => {
    const isNew = !seen.current.has(f.id); seen.current.add(f.id);
    let left = f.bbox.x * scale, top = (size.height - f.bbox.y - (f.bbox.h || 12)) * scale, w = (f.bbox.w || 0) * scale, h = (f.bbox.h || 12) * scale;
    if (vp) { const r = vp.convertToViewportRectangle([f.bbox.x, f.bbox.y, f.bbox.x + (f.bbox.w || 0), f.bbox.y + (f.bbox.h || 12)]); left = Math.min(r[0], r[2]); top = Math.min(r[1], r[3]); w = Math.abs(r[2] - r[0]); h = Math.abs(r[3] - r[1]); }
    return { f, left, top, w, h, isNew };
  }), [findings, scale, size.height, vp]);
  return (
    <div className="relative bg-white rounded-md shadow-sm overflow-hidden" style={{ width, minHeight: Math.floor(size.height * scale) }}>
      <canvas ref={canvasRef} className="block" />
      {marks.map(({ f, left, top, w, h, isNew }) => (
        <div key={f.id} title={f.message} className={`absolute rounded-sm pointer-events-auto ${isNew ? 'mr-pop' : ''} ${focusFindingId === f.id ? 'mr-focus' : ''}`}
          style={{ left: left - 3, top: top - 3, width: Math.max(10, w) + 6, height: h + 6, border: `2px solid ${COLOR[f.severity] || COLOR.note}`, background: f.severity === 'note' ? 'transparent' : 'rgba(229,96,31,0.10)', boxShadow: focusFindingId === f.id ? `0 0 0 4px ${COLOR[f.severity] || COLOR.note}33` : undefined }} />
      ))}
      <style>{`@keyframes mrPop{0%{transform:scale(1.25);opacity:0}60%{transform:scale(.96);opacity:1}100%{transform:scale(1)}}.mr-pop{animation:mrPop .45s ease-out}.mr-focus{z-index:2}`}</style>
    </div>
  );
}

function ImagePage({ src, findings, layout, width, height }) {
  const url = useObjectUrl(src);
  const p = layout?.pages?.[0];
  const scale = p?.width ? width / p.width : null;
  return (
    <div className="overflow-auto rounded-xl bg-slate-100 border border-slate-200 p-3" style={{ maxHeight: height }}>
      <div className="relative mx-auto bg-white rounded-md shadow-sm overflow-hidden" style={{ width }}>
        {url && <img src={url} alt="" className="block" style={{ width }} />}
        {scale && findings.filter((f) => f.bbox).map((f) => (
          <div key={f.id} title={f.message} className="absolute rounded-sm" style={{ left: f.bbox.x * scale - 3, top: (p.height - f.bbox.y - (f.bbox.h || 12)) * scale - 3, width: Math.max(10, f.bbox.w * scale) + 6, height: (f.bbox.h || 12) * scale + 6, border: `2px solid ${COLOR[f.severity] || COLOR.note}`, background: f.severity === 'note' ? 'transparent' : 'rgba(229,96,31,0.10)' }} />
        ))}
      </div>
    </div>
  );
}

/** Word files: the measured lines laid out at their sizes (no page picture exists). */
function TextPages({ layout, findings, focusPage, height, width }) {
  const scroller = useRef(null);
  const refs = useRef({});
  useEffect(() => { const el = focusPage && refs.current[focusPage]; if (el && scroller.current) scroller.current.scrollTo({ top: el.offsetTop - 12, behavior: 'smooth' }); }, [focusPage]);
  if (!layout?.pages?.length) return <div className="rounded-xl bg-slate-100 border border-slate-200 p-6 text-sm text-slate-500 text-center" style={{ minHeight: 240 }}>A Word file has no page picture. The text appears here, laid out line by line, once the file is read.</div>;
  return (
    <div ref={scroller} className="overflow-auto rounded-xl bg-slate-100 border border-slate-200 p-3" style={{ maxHeight: height }}>
      {layout.pages.map((p) => {
        const scale = width / (p.width || 612); const H = (p.height || 792) * scale;
        const marks = findings.filter((f) => f.page === p.page && f.bbox);
        return (
          <div key={p.page} ref={(el) => { refs.current[p.page] = el; }} className="mx-auto mb-3 last:mb-0" style={{ width }}>
            <div className="relative bg-white rounded-md shadow-sm overflow-hidden" style={{ width, height: H }}>
              {p.lines.map((l, i) => <div key={i} className="absolute text-slate-700 whitespace-nowrap overflow-hidden" style={{ left: l.x * scale, top: H - (l.y + (l.h || l.size || 12)) * scale, width: Math.max(4, l.w * scale), fontSize: Math.max(5, (l.size || 10) * scale), lineHeight: 1 }} title={`${l.size} pt`}>{l.text}</div>)}
              {marks.map((f) => <div key={f.id} title={f.message} className="absolute rounded-sm" style={{ left: f.bbox.x * scale - 3, top: H - (f.bbox.y + (f.bbox.h || 12)) * scale - 3, width: Math.max(10, f.bbox.w * scale) + 6, height: (f.bbox.h || 12) * scale + 6, border: `2px solid ${COLOR[f.severity] || COLOR.note}`, background: f.severity === 'note' ? 'transparent' : 'rgba(229,96,31,0.10)' }} />)}
            </div>
            <div className="text-[10px] text-slate-400 text-center mt-1">Page {p.page} of {layout.pages.length}{layout.kind === 'ocr' ? ' · scanned, sizes estimated' : ''}</div>
          </div>
        );
      })}
    </div>
  );
}
